import { useState, useEffect } from 'react'
import {
  registerWithEmail,
  loginWithEmail,
  logoutUser,
  subscribeToAuthState,
  firestoreSetDoc,
  firestoreGetDoc,
  firestoreServerTimestamp,
  initializeFirebase,
} from '../config/firebase'
import type { User } from '../config/firebase'

type Mode = 'signup' | 'login'

interface ProfileData {
  username: string
  email: string
  createdAt: unknown
  dob?: string
}

export default function ProfileScreen() {
  const [mode, setMode] = useState<Mode>('signup')
  const [user, setUser] = useState<User | null>(null)
  const [profile, setProfile] = useState<ProfileData | null>(null)

  const [username, setUsername] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [dob, setDob] = useState('')

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  useEffect(() => {
    let unsubscribe: (() => void) | undefined
    initializeFirebase().then(() => {
      unsubscribe = subscribeToAuthState(async (firebaseUser) => {
        setUser(firebaseUser)
        if (firebaseUser) {
          await loadProfile(firebaseUser.uid)
        } else {
          setProfile(null)
        }
      })
    })
    return () => unsubscribe?.()
  }, [])

  const loadProfile = async (uid: string) => {
    try {
      const data = await firestoreGetDoc('users', uid)
      if (data) setProfile(data as ProfileData)
    } catch (e) {
      console.error('Failed to load profile:', e)
    }
  }

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null); setSuccess(null)
    if (!username.trim()) { setError('Username is required.'); return }
    if (!email.trim()) { setError('Email is required.'); return }
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return }
    setLoading(true)
    try {
      const newUser = await registerWithEmail(email.trim(), password, username.trim())
      const profileData: ProfileData = {
        username: username.trim(),
        email: email.trim().toLowerCase(),
        createdAt: firestoreServerTimestamp(),
        ...(dob ? { dob } : {}),
      }
      await firestoreSetDoc('users', newUser.uid, profileData as unknown as Record<string, unknown>)
      setSuccess('Account created! Welcome to Rock AR.')
      setUsername(''); setEmail(''); setPassword(''); setDob('')
    } catch (err: unknown) {
      setError(friendlyAuthError(err))
    } finally {
      setLoading(false)
    }
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null); setSuccess(null)
    setLoading(true)
    try {
      await loginWithEmail(email.trim(), password)
      setEmail(''); setPassword('')
    } catch (err: unknown) {
      setError(friendlyAuthError(err))
    } finally {
      setLoading(false)
    }
  }

  const handleLogout = async () => {
    setError(null)
    await logoutUser()
  }

  // ── Logged-in view ──────────────────────────────────────────────────────
  if (user) {
    const displayName = profile?.username ?? user.displayName ?? 'User'
    const joinedDate = profile?.createdAt ? formatTimestamp(profile.createdAt) : '—'

    return (
      <div className="app-screen profile-screen">
        <div className="app-screen-header">
          <span className="app-screen-title">Profile</span>
        </div>
        <div className="app-screen-scroll">
          <div className="profile-card">
            <div className="profile-avatar">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </div>
            <div className="profile-info">
              <p className="profile-name">{displayName}</p>
              <p className="profile-handle">{user.email}</p>
            </div>
          </div>

          <div className="profile-stats">
            <div className="profile-stat">
              <p className="profile-stat-value">{joinedDate}</p>
              <p className="profile-stat-label">Joined</p>
            </div>
            {profile?.dob && (
              <>
                <div className="profile-stat-divider" />
                <div className="profile-stat">
                  <p className="profile-stat-value">{profile.dob}</p>
                  <p className="profile-stat-label">Birthday</p>
                </div>
              </>
            )}
          </div>

          <button className="profile-signout-btn" onClick={handleLogout}>
            Sign Out
          </button>
        </div>
      </div>
    )
  }

  // ── Auth form view ──────────────────────────────────────────────────────
  return (
    <div className="app-screen profile-screen">
      <div className="app-screen-header">
        <span className="app-screen-title">Profile</span>
      </div>
      <div className="app-screen-scroll">
        <div className="auth-card">
          <div className="auth-tabs">
            <button
              className={`auth-tab${mode === 'signup' ? ' active' : ''}`}
              onClick={() => { setMode('signup'); setError(null); setSuccess(null) }}
            >Sign Up</button>
            <button
              className={`auth-tab${mode === 'login' ? ' active' : ''}`}
              onClick={() => { setMode('login'); setError(null); setSuccess(null) }}
            >Log In</button>
          </div>

          <form className="auth-form" onSubmit={mode === 'signup' ? handleSignUp : handleLogin}>
            {mode === 'signup' && (
              <div className="auth-field">
                <label className="auth-label" htmlFor="auth-username">Username *</label>
                <input id="auth-username" className="auth-input" type="text"
                  autoComplete="username" placeholder="e.g. rockstar92"
                  value={username} onChange={e => setUsername(e.target.value)} required />
              </div>
            )}

            <div className="auth-field">
              <label className="auth-label" htmlFor="auth-email">Email *</label>
              <input id="auth-email" className="auth-input" type="email"
                autoComplete="email" placeholder="you@email.com"
                value={email} onChange={e => setEmail(e.target.value)} required />
            </div>

            <div className="auth-field">
              <label className="auth-label" htmlFor="auth-password">Password *</label>
              <input id="auth-password" className="auth-input" type="password"
                autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                placeholder="Min. 6 characters"
                value={password} onChange={e => setPassword(e.target.value)} required />
            </div>

            {mode === 'signup' && (
              <div className="auth-field">
                <label className="auth-label" htmlFor="auth-dob">
                  Date of Birth <span className="auth-optional">(optional)</span>
                </label>
                <input id="auth-dob" className="auth-input" type="date"
                  value={dob} onChange={e => setDob(e.target.value)} />
              </div>
            )}

            {error && <p className="auth-error" role="alert">{error}</p>}
            {success && <p className="auth-success" role="status">{success}</p>}

            <button className="auth-submit" type="submit" disabled={loading}>
              {loading ? 'Please wait…' : mode === 'signup' ? 'Create Account' : 'Log In'}
            </button>
          </form>

          <p className="auth-switch">
            {mode === 'signup'
              ? <>Already have an account?{' '}
                  <button className="auth-switch-link" onClick={() => { setMode('login'); setError(null) }}>Log in</button></>
              : <>Don't have an account?{' '}
                  <button className="auth-switch-link" onClick={() => { setMode('signup'); setError(null) }}>Sign up</button></>
            }
          </p>
        </div>
      </div>
    </div>
  )
}

function friendlyAuthError(err: unknown): string {
  const code = (err as { code?: string })?.code ?? ''
  const map: Record<string, string> = {
    'auth/email-already-in-use': 'That email is already registered.',
    'auth/invalid-email': 'Please enter a valid email address.',
    'auth/weak-password': 'Password must be at least 6 characters.',
    'auth/user-not-found': 'No account found with that email.',
    'auth/wrong-password': 'Incorrect password. Please try again.',
    'auth/invalid-credential': 'Incorrect email or password.',
    'auth/too-many-requests': 'Too many attempts. Please try again later.',
    'auth/network-request-failed': 'Network error. Check your connection.',
  }
  return map[code] ?? 'Something went wrong. Please try again.'
}

function formatTimestamp(ts: unknown): string {
  if (!ts) return '—'
  if (typeof (ts as { toDate?: () => Date }).toDate === 'function') {
    return (ts as { toDate: () => Date }).toDate().toLocaleDateString()
  }
  return '—'
}
